import React, { useCallback, useEffect, useRef } from "react";

interface UseSwipeProps {
    ref: React.RefObject<HTMLElement>;
    onLeft: () => void;
    onRight: () => void;
}

export function useSwipe({ ref, onLeft, onRight }: UseSwipeProps) {
    const startXRef = useRef(0);
    const startYRef = useRef(0);
    const activeRef = useRef(false);

    const handleTouchStart = useCallback((e: TouchEvent) => {
        if (e.touches.length !== 1) return;
        activeRef.current = true;
        startXRef.current = e.touches[0].pageX;
        startYRef.current = e.touches[0].pageY;
    }, []);

    const handleTouchMove = useCallback((e: TouchEvent) => {
        if (!activeRef.current) return;

        const dx = e.touches[0].pageX - startXRef.current;
        const dy = e.touches[0].pageY - startYRef.current;

        if (Math.abs(dx) > Math.abs(dy)) {
            e.preventDefault();

            const threshold = 10;
            if (dx < -threshold) {
                activeRef.current = false;
                onLeft();
            } else if (dx > threshold) {
                activeRef.current = false;
                onRight();
            }
        }
    }, [onLeft, onRight]);

    const handleTouchEnd = useCallback(() => {
        activeRef.current = false;
    }, []);

    useEffect(() => {
        if (!ref.current) return;

        const el = ref.current;
        const options = { passive: false };

        el.addEventListener("touchstart", handleTouchStart, options);
        el.addEventListener("touchmove", handleTouchMove, options);
        el.addEventListener("touchend", handleTouchEnd);

        return () => {
            el.removeEventListener("touchstart", handleTouchStart);
            el.removeEventListener("touchmove", handleTouchMove);
            el.removeEventListener("touchend", handleTouchEnd);
        };
    }, [ref, handleTouchStart, handleTouchMove, handleTouchEnd]);
}

